// Cookie Policy of superluxury.io — verbatim from the approved iubenda document (latest update: April 22, 2026).
import type { LegalDocument } from './legal';
import { definitionsSection, legalInformationSection, ownerDetails } from './legalShared';

export const cookiePolicy: LegalDocument = {
  slug: 'cookie-policy',
  title: 'Cookie Policy',
  updated: '22 April 2026',
  intro:
    'This document informs Users about the technologies that help this Website to achieve the purposes described below. Such technologies allow the Owner to access and store information (for example by using a Cookie) or use resources (for example by running a script) on a User’s device as they interact with this Website.',
  sections: [
    {
      heading: 'About this document',
      paragraphs: [
        'For simplicity, all such technologies are defined as "Trackers" within this document – unless there is a reason to differentiate. For example, while Cookies can be used on both web and mobile browsers, it would be inaccurate to talk about Cookies in the context of mobile apps as they are a browser-based Tracker. For this reason, within this document, the term Cookies is only used where it is specifically meant to indicate that particular type of Tracker.',
        "Some of the purposes for which Trackers are used may also require the User's consent, depending on the applicable law. Whenever consent is given, it can be freely withdrawn at any time following the instructions provided in this document.",
        'This Website uses Trackers managed directly by the Owner (so-called “first-party” Trackers) and Trackers that enable services provided by a third-party (so-called “third-party” Trackers). Unless otherwise specified within this document, third-party providers may access the Trackers managed by them.',
        'The validity and expiration periods of Cookies and other similar Trackers may vary depending on the lifetime set by the Owner or the relevant provider. Some of them expire upon termination of the User’s browsing session.',
        'In addition to what’s specified in the descriptions within each of the categories below, Users may find more precise and updated information regarding lifetime specification as well as any other relevant information — such as the presence of other Trackers — in the linked privacy policies of the respective third-party providers or by contacting the Owner.',
      ],
    },
    {
      heading: 'How this Website uses Trackers',
      paragraphs: [],
      subsections: [
        {
          heading: 'Necessary',
          paragraphs: [
            'This Website uses so-called “technical” Cookies and other similar Trackers to carry out activities that are strictly necessary for the operation or delivery of the Service.',
            'Trackers managed by third parties:',
          ],
        },
        {
          heading: 'Google Fonts (Google LLC)',
          paragraphs: [
            'Google Fonts is a typeface visualization service provided by Google LLC or by Google Ireland Limited, depending on how the Owner manages the Data processing, that allows this Website to incorporate content of this kind on its pages.',
            'Personal Data processed: Trackers and Usage Data.',
            'Place of processing: United States – Privacy Policy; Ireland – Privacy Policy.',
          ],
          minor: true,
        },
        {
          heading: 'Google Maps widget (Google Ireland Limited)',
          paragraphs: [
            'Google Maps is a maps visualization service provided by Google Ireland Limited that allows this Website to incorporate content of this kind on its pages.',
            'Personal Data processed: Trackers and Usage Data.',
            'Place of processing: Ireland – Privacy Policy.',
          ],
          minor: true,
        },
        {
          heading: 'Webflow (Webflow, Inc.)',
          paragraphs: [
            'Webflow is a platform provided by Webflow, Inc. that allows the Owner to build, run and host this Website. Webflow is highly customizable and can host websites from simple blogs to complex e-commerce platforms.',
            'Personal Data processed: Trackers, Usage Data and various types of Data as specified in the privacy policy of the service.',
            'Place of processing: United States – Privacy Policy – Opt out.',
          ],
          minor: true,
        },
        {
          heading: 'Measurement',
          paragraphs: [
            'This Website uses Trackers to measure traffic and analyze User behavior to improve the Service.',
            'Trackers managed by third parties:',
          ],
        },
        {
          heading: 'Google Analytics 4 (Google Ireland Limited)',
          paragraphs: [
            'Google Analytics 4 is a web analysis service provided by Google Ireland Limited (“Google”). Google utilizes the Data collected to track and examine the use of this Website, to prepare reports on its activities and share them with other Google services. Google may use the Data collected to contextualize and personalize the ads of its own advertising network. In Google Analytics 4, IP addresses are used at collection time and then discarded before Data is logged in any data center or server. Users can learn more by consulting Google’s official documentation.',
            "In order to understand Google's use of Data, consult their partner policy and their Business Data page.",
            'Personal Data processed: number of Users, session statistics, Trackers and Usage Data.',
            'Place of processing: Ireland – Privacy Policy – Opt out.',
            'Trackers duration:',
          ],
          list: ['_ga: 2 years', '_ga_*: 2 years'],
          minor: true,
        },
      ],
    },
    {
      heading: 'How to manage preferences and provide or withdraw consent on this Website',
      paragraphs: [
        'Whenever the use of Trackers is based on consent, users can provide or withdraw such consent by setting or updating their preferences via the relevant privacy choices panel available on this Website.',
        "With regard to any third-party Trackers, Users can manage their preferences via the related opt-out link (where provided), by using the means indicated in the third party's privacy policy, or by contacting the third party.",
      ],
      subsections: [
        {
          heading: 'How to control or delete Cookies and similar technologies via your device settings',
          paragraphs: ['Users may use their own browser settings to:'],
          list: [
            'See what Cookies or other similar technologies have been set on the device;',
            'Block Cookies or similar technologies;',
            'Clear Cookies or similar technologies from the browser.',
          ],
          after: [
            'The browser settings, however, do not allow granular control of consent by category.',
            'Users can, for example, find information about how to manage Cookies in the most commonly used browsers at the following addresses: Google Chrome, Mozilla Firefox, Apple Safari, Microsoft Internet Explorer, Microsoft Edge, Brave, Opera.',
            'Users may also manage certain categories of Trackers used on mobile apps by opting out through relevant device settings such as the device advertising settings for mobile devices, or tracking settings in general (Users may open the device settings and look for the relevant setting).',
          ],
        },
        {
          heading: 'Consequences of denying the use of Trackers',
          paragraphs: [
            'Users are free to decide whether or not to allow the use of Trackers. However, please note that Trackers help this Website to provide a better experience and advanced functionalities to Users (in line with the purposes outlined in this document). Therefore, if the User chooses to block the use of Trackers, the Owner may be unable to provide related features.',
          ],
        },
      ],
    },
    {
      heading: 'Owner and Data Controller',
      paragraphs: ownerDetails,
      after: [
        'Owner contact email: hey@superluxury.io',
        'Since the use of third-party Trackers through this Website cannot be fully controlled by the Owner, any specific references to third-party Trackers are to be considered indicative. In order to obtain complete information, Users are kindly requested to consult the privacy policies of the respective third-party services listed in this document.',
        'Given the objective complexity surrounding tracking technologies, Users are encouraged to contact the Owner should they wish to receive any further information on the use of such technologies by this Website.',
      ],
      compact: true,
    },
    definitionsSection,
    legalInformationSection,
  ],
};
